import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiCardView } from '@euler/model/bi/dashboard';
import { ReactNode, memo } from 'react';
import { View } from 'react-native';
import { BiCardKind, biCardKind } from '../../functions/biCardKind';
import {
  BiPresetRow,
  biCardFits,
  biCardRows,
  biPresetRowRows,
  biRowsHeight,
  kBiGridGapPx,
} from '../../functions/biGridLayout';

/**
 * Desktop preset: cards flow into 2 or 3 columns in order, a `full` card
 * across the row; a row cut short by one shares out the whole width, so
 * no column stays empty. Each row is as tall as its tallest card; a row
 * of KPI groups as tall as their tiles draw.
 */
export const PresetGrid = memo(
  ({
    rows,
    width,
    cards,
    renderCard,
  }: {
    rows: BiPresetRow[];
    width: number;
    cards: Map<string, BiCardView>;
    renderCard: (card: BiCardView, height: number | undefined) => ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const kindOf = (id: string) => {
      const card = cards.get(id);
      return card ? biCardKind(card.viz) : BiCardKind.Chart;
    };
    return (
      <View style={{ gap: kBiGridGapPx }}>
        {rows.map(row => {
          const count = row.cardIds.length;
          const cardWidth = (width - kBiGridGapPx * (count - 1)) / count;
          // A row of KPI groups fits their tiles (the row as tall as the
          // tallest, the others stretched to it); any other takes grid rows.
          const fits = row.cardIds.every(id => {
            const card = cards.get(id);
            return !!card && biCardFits(card.viz);
          });
          const height = fits
            ? undefined
            : biRowsHeight(
                biPresetRowRows(row, kindOf, id => {
                  const card = cards.get(id);
                  return card ? biCardRows(card.viz, cardWidth, rpx) : null;
                }),
              );
          return (
            <View
              key={row.cardIds.join(',')}
              style={{ flexDirection: 'row', gap: kBiGridGapPx }}
            >
              {row.cardIds.map(id => {
                const card = cards.get(id);
                if (!card) return null;
                return (
                  <View key={id} style={{ width: cardWidth }}>
                    {renderCard(card, height)}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  },
);
